import { sql } from 'drizzle-orm';
import {
  pgTable,
  pgPolicy,
  index,
  uniqueIndex,
  jsonb,
  timestamp,
  varchar,
  text,
  integer,
  boolean,
  uuid,
  primaryKey
} from "drizzle-orm/pg-core";
import { organizations } from './organizations';
import { users } from './users';

// Session storage table (required for Replit Auth)
export const sessions = pgTable(
  "sessions",
  {
    sid: varchar("sid").primaryKey(),
    sess: jsonb("sess").notNull(),
    expire: timestamp("expire").notNull(),
  },
  (table) => [index("IDX_session_expire").on(table.expire)],
);


// Integrations table for storing third-party service configurations
export const integrations = pgTable("integrations", {
  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),
  id: varchar("id").notNull(), // 'twilio', 'retell', 'sendgrid', etc.
  config: jsonb("config").notNull().default({}), // Store API keys and settings here
  isEnabled: boolean("is_enabled").default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  updatedBy: varchar("updated_by"), // User ID who last updated the config
}, (table) => [
  primaryKey({ columns: [table.orgId, table.id] }),
  pgPolicy('integrations_tenant_isolation', {
    as: 'permissive',
    for: 'all',
    to: 'app_user',
    using: sql`org_id = current_setting('app.current_tenant', true)::uuid`,
    withCheck: sql`org_id = current_setting('app.current_tenant', true)::uuid`,
  }),
]).enableRLS();


// System Settings table for global configuration
export const systemSettings = pgTable("system_settings", {
  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),
  id: varchar("id").notNull().default('default'),

  // Email Automation Settings
  emailAutomationEnabled: boolean("email_automation_enabled").default(true), // Controls automatic email processing (Smart Sync bulk import)
  emailSyncEnabled: boolean("email_sync_enabled").default(true), // Controls email syncing to existing claims

  // Other Global Settings
  maintenanceMode: boolean("maintenance_mode").default(false),
  aiReviewIntervalDays: integer("ai_review_interval_days").default(14).notNull(),
  smartSyncDailyLimit: integer("smart_sync_daily_limit").default(500).notNull(), // Max emails auto-processed per user per day; 0 = unlimited

  updatedAt: timestamp("updated_at").defaultNow(),
  updatedBy: varchar("updated_by"),
}, (table) => [
  primaryKey({ columns: [table.orgId, table.id] }),
  pgPolicy('system_settings_tenant_isolation', {
    as: 'permissive',
    for: 'all',
    to: 'app_user',
    using: sql`org_id = current_setting('app.current_tenant', true)::uuid`,
    withCheck: sql`org_id = current_setting('app.current_tenant', true)::uuid`,
  }),
]).enableRLS();


// AI Configuration table for storing API keys and per-feature model overrides
export const aiConfig = pgTable("ai_config", {
  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),
  id: varchar("id").notNull().default(sql`gen_random_uuid()`),
  configKey: varchar("config_key").notNull(), // e.g. "openai_api_key", "feature_model.policy_analysis"
  configValue: text("config_value").notNull(), // API key value or model name
  isSecret: boolean("is_secret").default(false), // If true, value is masked in API responses
  label: varchar("label"), // Human-readable label for UI
  description: text("description"), // Help text for the config entry
  category: varchar("category").notNull().default("general"), // "api_key", "feature_model"
  updatedAt: timestamp("updated_at").defaultNow(),
  updatedBy: varchar("updated_by"),
}, (table) => [
  primaryKey({ columns: [table.orgId, table.id] }),
  uniqueIndex('ai_config_org_key_unique').on(table.orgId, table.configKey),
  pgPolicy('ai_config_tenant_isolation', {
    as: 'permissive',
    for: 'all',
    to: 'app_user',
    using: sql`org_id = current_setting('app.current_tenant', true)::uuid`,
    withCheck: sql`org_id = current_setting('app.current_tenant', true)::uuid`,
  }),
]).enableRLS();


// Company Settings table for company information
export const companySettings = pgTable("company_settings", {
  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),
  id: varchar("id").notNull().default('default'),
  companyName: varchar("company_name").notNull(),
  address: text("address"),
  phone: varchar("phone"),
  email: varchar("email"),
  website: varchar("website"),
  taxId: varchar("tax_id"),
  updatedAt: timestamp("updated_at").defaultNow(),
  updatedBy: varchar("updated_by"),

  // Branding
  logoUrl: varchar("logo_url"),           // Full logo URL (for login, PDF exports)
  logoSmallUrl: varchar("logo_small_url"), // Small logo (sidebar icon)
  faviconUrl: varchar("favicon_url"),      // Favicon URL
  tagline: varchar("tagline"),             // e.g. "Property Loss Consultants"
  siteName: varchar("site_name"),          // Display name for navbar/browser tab (overrides companyName)
  primaryColor: varchar("primary_color"),  // Hex color, e.g. "#1a73e8"
  accentColor: varchar("accent_color"),    // Hex color for accents
  timezone: varchar("timezone").notNull().default("America/New_York"), // Operating timezone

  // Configurable Business Rules
  paThresholdMajorOffice: integer("pa_threshold_major_office").default(100000),
  paThresholdRemote: integer("pa_threshold_remote").default(500000),
  serviceTypes: jsonb("service_types"),    // Array of enabled services: ["pa", "appraisal", "consulting"]
}, (table) => [
  primaryKey({ columns: [table.orgId, table.id] }),
  pgPolicy('company_settings_tenant_isolation', {
    as: 'permissive',
    for: 'all',
    to: 'app_user',
    using: sql`org_id = current_setting('app.current_tenant', true)::uuid`,
    withCheck: sql`org_id = current_setting('app.current_tenant', true)::uuid`,
  }),
]).enableRLS();


// Enhanced audit logs table for comprehensive enterprise tracking
export const auditLogs = pgTable("audit_logs", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id, { onDelete: "set null" }),
  action: varchar("action").notNull(), // CREATE, UPDATE, DELETE, LOGIN, LOGOUT, APPROVE, etc.
  entityType: varchar("entity_type").notNull(), // claims, contacts, documents, users, etc.
  entityId: varchar("entity_id").notNull(),
  oldValues: jsonb("old_values"), // Complete previous state for UPDATE/DELETE
  newValues: jsonb("new_values"), // Complete new state for CREATE/UPDATE
  changedFields: jsonb("changed_fields"), // Array of field names that changed
  sessionId: varchar("session_id"), // Session identifier for tracking
  ipAddress: varchar("ip_address"),
  userAgent: text("user_agent"),
  requestMethod: varchar("request_method"), // GET, POST, PUT, DELETE
  requestPath: varchar("request_path"), // API endpoint accessed
  isSuccessful: boolean("is_successful").default(true), // Track failed operations
  errorMessage: text("error_message"), // Store error details for failed operations
  metadata: jsonb("metadata"), // Additional context-specific data
  previousHash: varchar("previous_hash", { length: 64 }), // SHA-256 hash of previous audit log (hash chain)
  currentHash: varchar("current_hash", { length: 64 }), // SHA-256 hash of current log entry (immutability)
  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_audit_logs_user_id").on(table.userId),
  index("idx_audit_logs_entity").on(table.entityType, table.entityId),
  index("idx_audit_logs_created_at").on(table.createdAt),
  index("idx_audit_logs_action").on(table.action),
  index("idx_audit_logs_current_hash").on(table.currentHash),
  pgPolicy('audit_logs_tenant_isolation', {
    as: 'permissive',
    for: 'all',
    to: 'app_user',
    using: sql`org_id = current_setting('app.current_tenant', true)::uuid`,
    withCheck: sql`org_id = current_setting('app.current_tenant', true)::uuid`,
  }),
]).enableRLS();


// API Keys table for external API access
export const apiKeys = pgTable("api_keys", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  label: varchar("label", { length: 255 }).notNull(),
  key: varchar("key", { length: 128 }).notNull(),
  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_api_keys_key").on(table.key),
  pgPolicy('api_keys_tenant_isolation', {
    as: 'permissive',
    for: 'all',
    to: 'app_user',
    using: sql`org_id = current_setting('app.current_tenant', true)::uuid`,
    withCheck: sql`org_id = current_setting('app.current_tenant', true)::uuid`,
  }),
]).enableRLS();


// Audit action types for type safety
export const AUDIT_ACTIONS = {
  // CRUD Operations
  CREATE: 'CREATE',
  UPDATE: 'UPDATE', 
  DELETE: 'DELETE',
  READ: 'READ', // For sensitive data access
  
  // Authentication & Authorization
  LOGIN: 'LOGIN',
  LOGOUT: 'LOGOUT',
  PERMISSION_GRANT: 'PERMISSION_GRANT',
  PERMISSION_REVOKE: 'PERMISSION_REVOKE',
  
  // Business Operations
  APPROVE: 'APPROVE',
  REJECT: 'REJECT',
  SUBMIT: 'SUBMIT',
  CANCEL: 'CANCEL',
  
  // Document Operations
  UPLOAD: 'UPLOAD',
  DOWNLOAD: 'DOWNLOAD',
  SHARE: 'SHARE',
  
  // Communication Operations
  SEND_EMAIL: 'SEND_EMAIL',
  RECEIVE_EMAIL: 'RECEIVE_EMAIL',
  
  // Financial Operations
  PAYMENT_RECEIVED: 'PAYMENT_RECEIVED',
  INVOICE_GENERATED: 'INVOICE_GENERATED',
  COMMISSION_CALCULATED: 'COMMISSION_CALCULATED',
} as const;


// Entity types for audit logging
export const AUDIT_ENTITIES = {
  CLAIMS: 'claims',
  CONTACTS: 'contacts', 
  DOCUMENTS: 'documents',
  COMMUNICATIONS: 'communications',
  USERS: 'users',
  ROLES: 'roles',
  PERMISSIONS: 'permissions',
  USER_ROLES: 'user_roles',
  ESTIMATES: 'estimates',
  OFFERS: 'offers',
  PAYMENTS: 'payments',
  INVOICES: 'invoices',
  CLAIM_PA_ASSIGNMENTS: 'claim_pa_assignments',
  COMMISSIONS: 'commissions',
  TASKS: 'tasks',
  RETENTION_POLICIES: 'retention_policies',
  BREACH_NOTIFICATIONS: 'breach_notifications',
  DATA_DELETION_REQUESTS: 'data_deletion_requests',
  AUDIT_LOGS: 'audit_logs',
  TIMELINE_EVENTS: 'timeline_events',
  EMAIL_ACCOUNTS: 'email_accounts',
  AI_DRAFTS: 'ai_drafts',
  CARRIERS: 'carriers',
  LEADS: 'leads',
  TIMESHEETS: 'timesheets',
  DOCUMENT_TEMPLATES: 'document_templates',
  DOCUMENT_GENERATIONS: 'document_generations',
  DOCUSIGN_ENVELOPES: 'docusign_envelopes',
  INTAKE_DECISIONS: 'intake_decisions',
  ESTIMATE_LEARNING: 'estimate_learning',
  FINANCIAL: 'financial',
  AI_FEEDBACK: 'ai_feedback',
  SYSTEM: 'system',
  PROCESSED_EMAILS: 'processed_emails',
  FEEDBACK_ITEMS: 'feedback_items',
  FIRM_EXPORTS: 'firm_exports',
} as const;


// Per-org configurable weekly usage limits
export const orgUsageLimits = pgTable("org_usage_limits", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: varchar("org_id").notNull().unique(), // FK to organizations.id when that model is fully live
  // Weekly limits (null = unlimited / no bar shown)
  aiCallsWeekly: integer("ai_calls_weekly"),
  aiTokensWeekly: integer("ai_tokens_weekly"),
  twilioCallsWeekly: integer("twilio_calls_weekly"),
  docusignEnvelopesWeekly: integer("docusign_envelopes_weekly"),
  gmailSendsWeekly: integer("gmail_sends_weekly"),
  updatedAt: timestamp("updated_at").defaultNow(),
  updatedBy: varchar("updated_by"),
});


// AI Agent constants (deprecated — kept for backward compat with legacy messages)
export const AI_AGENT_USER_ID = "ai-agent";

export function isAIAgentUser(userId: string): boolean {
  return userId === AI_AGENT_USER_ID;
}
